import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminCreateUserAttributeMutation,
  InternalAdminCreateUserAttributeMutationVariables,
} from "gql/graphql";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import colors from "app/colors";
import { SERVER_ERROR } from "modules/utils/Constants";
import { getMessageFromError } from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";
import { InfoIcon } from "modules/icons";
import useQueryString from "modules/hooks/use-query-string";
import InternalAdminCreateUserAttributeSelect, {
  INewUserAttributeOption,
} from "../components/internal-admin-create-user-attribute-select";
import INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE_MUTATION from "../_networking/internal-admin-create-user-attribute";
import useInternalAdminUserAttributeList from "../hooks/use-internal-admin-user-attribute-list";

interface IProps {
  userAttributeCategoryId: string;
  onSaveCallback?: Function;
}

const InternalAdminCreateUserAttributeModal = ({
  userAttributeCategoryId,
  onSaveCallback,
}: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();
  const [value, setValue] = useState<readonly INewUserAttributeOption[]>([]);

  const [internalAdminCreateUserAttributeMutationAPI, { loading }] =
    useMutation<
      InternalAdminCreateUserAttributeMutation,
      InternalAdminCreateUserAttributeMutationVariables
    >(INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE_MUTATION);

  const { userAttributes } = useInternalAdminUserAttributeList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
    },
  });

  const hasDuplicate = useMemo(() => {
    if (!userAttributes || userAttributes.length === 0) {
      return false;
    }
    const currentValues = value.map((value) => value.value.toLowerCase());
    return userAttributes.some((userAttribute) =>
      currentValues.includes(userAttribute.name.toLowerCase()),
    );
  }, [userAttributes, value]);

  const {
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = useCallback(async () => {
    if (!qsOrganizationId) return;
    await Promise.all(
      value.map((name) =>
        internalAdminCreateUserAttributeMutationAPI({
          variables: {
            organizationId: qsOrganizationId,
            input: {
              name: name.value,
              userAttributeCategoryId,
            },
          },
        }),
      ),
    );
    if (onSaveCallback) {
      onSaveCallback();
    }
    closeModal();
  }, [
    closeModal,
    internalAdminCreateUserAttributeMutationAPI,
    onSaveCallback,
    qsOrganizationId,
    userAttributeCategoryId,
    value,
  ]);

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Text fontWeight={600} fontSize={24} textAlign="center">
            Add User Attributes
          </Text>
        </ModalHeader>
        <ModalBody>
          <form id="admin-add-user-attribute" onSubmit={handleSubmit(onSubmit)}>
            <VStack w="full" spacing={2} alignItems="flex-start">
              <Text
                fontSize={16}
                fontWeight={700}
                color={colors.blackSecondary}
              >
                Attributes
              </Text>
              <Text fontSize={12} fontWeight={400} color={colors.gray30}>
                Type words and add a comma or hit return to set attribute
              </Text>
              <InternalAdminCreateUserAttributeSelect
                userAttributeCategoryId={userAttributeCategoryId}
                onChange={setValue}
              />
              {hasDuplicate && (
                <HStack w="full" bg={colors.redLight} p={2} borderRadius={8}>
                  <InfoIcon fill={colors.red} />
                  <Text color={colors.red} fontWeight={500} fontSize={14}>
                    One or more attributes already exist
                  </Text>
                </HStack>
              )}
              {errors.SERVER_ERROR && (
                <HStack w="full" bg={colors.redLight} p={2} borderRadius={8}>
                  <InfoIcon fill={colors.red} />
                  <Text color={colors.red} fontWeight={500} fontSize={14}>
                    {getMessageFromError(errors?.SERVER_ERROR?.message)}
                  </Text>
                </HStack>
              )}
            </VStack>
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                  isDisabled={loading || hasDuplicate || value.length === 0}
                >
                  {loading ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminCreateUserAttributeModal);
